import { apiClient } from '../../core/api';
import { wallClockIn } from '@lib/utils/wallClock';

/**
 * The Store Manager's message feed (#5.5).
 *
 * <p>Read-only from the client's side. Messages are composed server-side — by the local-08:00 briefing
 * sweep and by the onboarding congrats — so there is no "post" call: a public write would let anything
 * put words in the Store Manager's mouth. PRD V1.24 also removed the feed's preset question chips and
 * deferred free-form questions to V2, so there is no ask/answer call either.
 */

export type StoreManagerMessageType = 'briefing' | 'congrats';

export interface StoreManagerMessage {
  id: string;
  type: StoreManagerMessageType;
  /** The MERCHANT'S local date — what the TODAY / YESTERDAY separators group on. */
  day: string;
  /**
   * Structured content, never prose. The Store Manager is a program, so it composes fields rather
   * than sentences and the client owns the wording — which is also why a copy change never needs a
   * backend deploy.
   */
  payload: BriefingPayload | CongratsPayload | Record<string, unknown> | null;
  /** Where each element of the payload routes, keyed like `ledger.marketing`. */
  deepLinks: Record<string, string> | null;
  createdAt: string;
  unread: boolean;
}

/** One headline figure. `value` is null when the source returned nothing for that day —
 *  distinct from 0, which is a real "none happened" and must render as such. */
export interface BriefingMetric {
  value: number | null;
  /** Change against the comparison baseline, as a fraction (0.12 = +12%). Null when there is
   *  no baseline yet — a brand-new store has nothing to compare with. */
  deltaPct?: number | null;
}

/** PRD #5.2 segment 1 — yesterday's numbers. */
export interface BriefingNumbers {
  revenue?: BriefingMetric;
  /** ISO currency of `revenue`, from the store. Never assumed: a merchant selling in AUD must
   *  not be shown a $ that means USD. */
  currency?: string;
  orders?: BriefingMetric;
  sessions?: BriefingMetric;
  /** A fraction, not a percentage: 0.023 renders as 2.3%. */
  conversionRate?: BriefingMetric;
  /**
   * Why the deltas are missing, when they are. Shown verbatim under the heading, because
   * "no comparison" and "flat against last week" look identical on a tile otherwise.
   */
  comparisonNote?: string;
}

/**
 * PRD #14 — 简报的「今日通知」。
 *
 * <p>V0.9 把原来的「异动段(anomalies)」和「今日建议段(suggestions)」合成了一条列表：四段变三段
 * （昨日数字 → 今日通知 → AI 团队总账）。两段本来就是同一件事的两半 —— 异动说「卖光了」，建议说
 * 「去补货」—— 拆开之后商家要在一张卡里读两遍同一个商品。
 *
 * <p>`text` 是一句完整的祈使句（「Restock The Band Vintage Graphic Tee — U — it sold 38 in the
 * last 30 days and is now at zero」），`subject` 是句中要加粗的那段商品名。加粗在客户端做而不是让
 * 后端发 HTML：一条消息是冻结的 payload，往里塞标记就等于把样式冻进数据库。
 *
 * <p>后端尚未下发这个字段。缺省时前端由 anomalies + suggestions 合并兜底（见 BriefingCard），
 * 这样 2026-09 之前已经存下的四段式简报翻回去看也是三段的样子。
 */
export interface BriefingNotification {
  id: string;
  text: string;
  /** `text` 里要加粗的商品名；缺省则整句常规字重。 */
  subject?: string;
  /** `critical` 读作红点，其余读作紫点。阈值跟着触发它的规则走，不在样式表里。 */
  severity?: 'critical' | 'warning' | 'info';
  /** Go 按钮的去处；缺省则这一行不带按钮。 */
  href?: string;
}

/** PRD #5.2 segment 2 — what needs watching. 已并入 `notifications`，保留给旧 payload 读取。 */
export interface BriefingAnomaly {
  id: string;
  /** `critical` reads red, anything else reads amber. Severity comes from the server so the
   *  threshold lives with the rule that fired it, not in the stylesheet. */
  severity?: 'critical' | 'warning' | 'info';
  label: string;
  href?: string;
  /**
   * 这一行的第二行小字（CS 2.0 需求 8：转人工行的邮箱 /「Longest wait: 18 hours」）。缺省即只有标题。
   * 老简报和不发它的后端都是缺省，所以可选。
   */
  detail?: string;
  /**
   * 按钮文案，不带箭头（箭头由页面画）——「Open conversations」「Product Management」……
   * （CS 2.0 需求 8，定稿 26379:315892）。缺省时页面回落「Open」。
   */
  actionLabel?: string;
}

/** PRD #5.2 segment 4 — one thing worth doing today. */
export interface BriefingSuggestion {
  id: string;
  text: string;
  ctaLabel?: string;
  href?: string;
}

/**
 * [需求⑱ Marketing 简报块·Ads 日读] Maya 贡献给每日简报的广告解读——与广告看板顶部的 Maya's read
 * 同一次生成、两处复用（PRD ⑱①）。后端就绪前前端用 mock 兜底渲染。
 * 空态：无在投 campaign 则整块不出（campaignsLive 为 0/缺省时前端不渲染）。
 */

/**
 * The Monday weekly-report summary card (PRD 行[1] 運營報告定期推送).
 *
 * Three figures, not the report's four: PRD's summary row is GMV / Orders / Avg
 * order, and `direction` is COPIED from the report rather than recomputed here.
 * The ±5% band lives in one place, and a card calling a 2% week "up" while the
 * report page calls it flat would be two answers about one week.
 */
export interface WeeklyReportSummarySegment {
  reportId?: string;
  /**
   * The store's own zone rides along on `period` — the card renders the as-of moment in it.
   *
   * A moment shown in the VIEWER's zone puts the refund cut-off on the wrong side of half a day,
   * which is the whole point of the sentence it appears in (`weeklyReportAsOf`).
   */
  period?: { from?: string; to?: string; timezone?: string } | null;
  /**
   * When the figures were TRUE — the report's cut-off, not when the card was pushed.
   *
   * 🔴 Declared because the card now says it out loud (Joanna, YEP-809 第 2 条: the card and the
   * page it opens must agree about the data's cut-off). It was missing from BOTH ends: the
   * producer never forwarded it (`OperationAlertBriefingFeed.cardFor`) and this interface never
   * declared it, so the as-of line resolved to null and rendered nothing at all — while the
   * schedule sentence it replaced had been deleted. The two halves land together.
   */
  generatedAt?: string | null;
  /**
   * Whether this report has a refund figure at all — computed by the producer, not inferrable here.
   *
   * The card's as-of sentence states the refund cut-off rule, and must not state it for a week
   * whose refunds walk failed. The page can decide for itself (it has `kpis.refunds`); this card
   * carries only PRD #1's three tiles, refunds not among them, so the answer travels with it.
   * Absent on payloads pushed before the producer began sending it — treated as readable, which
   * is what those cards already assumed.
   */
  refundsReadable?: boolean | null;
  /**
   * Whether the order read that produced these figures ran out of pages.
   *
   * 🔴 The card renders a period-over-period arrow on each of its three tiles, and an arrow
   * computed off a truncated read can point the WRONG WAY — a floor divided by a floor is
   * neither a floor nor a ceiling. The report PAGE has had this flag all along and withholds its
   * chips on it; the card did not carry it, so it drew the arrows anyway: one week, two surfaces,
   * opposite claims. Absent on payloads pushed before the producer began sending it — treated as
   * NOT truncated, which is what those cards already assumed.
   */
  truncated?: boolean | null;
  /**
   * Whether the COMPARISON period's read ran out of pages — the half that decides the arrows.
   *
   * 🔴 Kept separate from `truncated` because they withhold different things: a short CURRENT
   * period makes the totals floors, a short PRIOR one leaves them exact and makes every arrow
   * off them untrustworthy. And that is the direction that invents good news — a real prior of
   * 200 read as 50 against a complete 100 reports +100%, green, on screen. It used to reach
   * consumers only as prose (`COMPARISON_TRUNCATED` in `moneyNotes`), which nothing could branch
   * on. Absent → treated as not truncated.
   */
  comparisonTruncated?: boolean | null;
  currency?: string | null;
  label?: string;
  /**
   * `previous` is the prior period's own figure, and it is what the comparison card draws its
   * "Last Week" bar from when it is there.
   *
   * 🔴 Declared because deriving the prior as `value / (1 + deltaPct)` — which is all the card
   * could do without it — is undefined exactly when the comparison matters most. A week that fell
   * to zero carries `deltaPct: -1`, so the divisor is 0, every bar is dropped as "no honest prior",
   * and the card returns null: the merchant asks "compare to last week", the tool runs, and
   * nothing appears. Observed on dev 2026-09-14. The figure was never missing — the snapshot has
   * had it all along and the report table prints it in its own Previous column.
   *
   * Optional, and the derivation stays as the fallback: a producer that has not shipped the field
   * yet keeps working exactly as before, and there is still only ever ONE prior on screen because
   * this one wins whenever it exists.
   */
  headline?: Record<
    string,
    {
      value?: number | null;
      previous?: number | null;
      deltaPct?: number | null;
      direction?: string | null;
    }
  >;
  /**
   * PRD 行[1] Highlights — one line each on sales, orders and products.
   *
   * Oscar's OWN per-section banners from the report, not a second piece of
   * writing. Short of three, or absent entirely, when narration only partly ran —
   * a normal outcome under the global LLM fallback, and better than a card that
   * invents a sentence to fill a slot.
   */
  highlights?: Record<string, string>;
  /** The leader of each ranking, with the same picture the report shows. */
  topProducts?: Record<string, {
    title?: string | null;
    units?: number | null;
    revenue?: number | null;
    imageUrl?: string | null;
    unitsDeltaPct?: number | null;
    unitsDirection?: string | null;
    /**
     * This product's share of the week's GMV, as a FRACTION (0.47 → "47% of GMV").
     *
     * Absent whenever the producer could not take an honest one: the store could not be read, or
     * the two figures disagree on basis badly enough to put one product above 100% of its own
     * store (product rows are before refunds, GMV is after). The tile shows the money alone then.
     */
    revenueShare?: number | null;
  }>;
}

/**
 * [Infra 1.5 #7] 供稿 agent 的一块。
 *
 * <p>PRD #7 要的是「各 agent 供稿格式」：简报中间夹的那些块<b>不是简报自己算出来的</b>，
 * 是各 agent 供稿进来的，每个供稿方声明四项 —— <b>块名、频率、出现条件、总账口径</b>。
 * 前端这边对应一张<b>块注册表</b>（<code>briefingBlocks.tsx</code>），一块一个 <code>key</code>。
 *
 * <p><b>这是前端提出的形状，后端还没有下发。</b>要点三条：
 * <ol>
 *   <li><code>key</code> 与注册表对齐，是唯一的关联方式 —— 不要按数组顺序对应；</li>
 *   <li><code>name</code> / <code>frequency</code> 是<b>供稿方声明的</b>，payload 带的优先于注册表里那份 ——
 *       供稿方给块改个名不该等前端发版；</li>
 *   <li><code>data</code> 的形状<b>由块自己定</b>，所以这里是 <code>unknown</code>，
 *       各块在注册表里自己收窄（见 <code>briefingBlocks.tsx</code> 的 <code>asAdsDailyData</code>）。</li>
 * </ol>
 *
 * <p><b>出现与否由供稿方决定</b>：这一块在不在这条简报里，就是它今天该不该出现。
 * 前端<b>不按日期重算</b>频率 —— 每条简报都是一份冻结的记录，
 * 如果前端自己判断「今天是周一」，同一条旧简报在不同时候翻到会显示不同的块。
 */
export interface BriefingBlockEnvelope {
  /** 与前端注册表的 key 对齐。 */
  key: string;
  /** 块名（商家读的）。缺省时用注册表里那份。 */
  name?: string | null;
  /** 供稿 agent 的 type，与 ledger 同一套取值。 */
  agentType?: string | null;
  /** 供稿方声明的频率，如 `daily` / `weekly`。目前只做展示与自查，不参与渲染判定。 */
  frequency?: string | null;
  /** 供稿方声明的出现条件，一句人话。同上，不参与渲染判定。 */
  condition?: string | null;
  /** 该块自己的数据。形状由块自己定，注册表里收窄。 */
  data?: unknown;
}

/** PRD #5.2's segments. Absent ones are listed in `unavailable` — see `BriefingPayload`. */
export interface BriefingPayload {
  /** The day this briefing REPORTS on (the day before it was pushed). */
  reportedOn?: string;
  numbers?: BriefingNumbers;
  /**
   * 周一的运营报告摘要卡片，在那一周的报告被冻结之后出现。
   *
   * <p>🔴 <b>这个字段的状态反转过两次，读注释前先看日期。</b>
   * 2026-08-26 从简报里删掉（3f616dd52 / #1907），这里一度标着「NO LONGER SENT,
   * and not rendered」；2026-09-19 按裁定加回来 —— 裁定（fuxu）：
   * <b>这一页以 Infra 1.5 为准</b>，它的简报页是五段式，第四段就是运营报告摘要。
   *
   * <p>所以现在：后端 {@code StoreManagerBriefingService.SEGMENT_WEEKLY_REPORT} 会发它，
   * 前端 {@code StoreManagerPageInfra15} 用 {@code WeeklyReportSegment} 渲染它。
   * <b>别照着上一版注释再删一次。</b>
   *
   * <p>⚠️ 8-26 那次删除还带了一个 migration 把这个字段从已存的行里剥掉，
   * 所以那之前的历史简报翻回去看仍然没有这一段 —— 那是既成事实，不是 bug。
   */
  weeklyReport?: WeeklyReportSummarySegment;
  /**
   * PRD #14 的今日通知段。**新 payload 只发这一个**；下面两个是 2026-09 之前存下的旧结构，
   * 仍然声明是因为消息是冻结的，翻回去看的那些卡片永远不会长出新字段。
   */
  notifications?: BriefingNotification[];
  anomalies?: BriefingAnomaly[];
  suggestions?: BriefingSuggestion[];
  /**
   * [Infra 1.5 #7] 各 agent 供稿的块，夹在「今日通知」与「AI 团队总账」之间。
   *
   * <p>顺序<b>不由 payload 决定</b>：注册表里的顺序才是屏上的顺序（PRD 的 Ads 日读在运营报告摘要之前），
   * 否则供稿顺序一变商家的简报就换了个样子。payload 只回答「这一块今天有没有」。
   */
  blocks?: BriefingBlockEnvelope[];
  /** [需求⑱①] Marketing 的 Ads 日读块（缺省时前端 mock 兜底）。 */
  ledger?: {
    totalTasks?: number;
    agents?: Array<{
      agentType: string;
      /** The name the merchant knows — "Maya", not "marketing". */
      label: string;
      total: number;
      done: number;
      awaitingApproval: number;
      failed: number;
      /**
       * [需求⑱] Maya's row counts content, not tasks: 「昨日发布 N 篇 / 生成 N 篇 / 在跑任务 M 条」.
       *
       * <p>Optional, and optional forever. Only the marketing row carries them — a conversation is
       * not measured in 篇 — and every briefing composed before this shipped has none, because a
       * message is a FROZEN payload rather than a view that gets recomputed. So their absence is
       * the normal case for scroll-back and must fall through to the task wording, not to zeroes.
       */
      publishedCount?: number;
      generatedCount?: number;
      runningCount?: number;
      /**
       * OBS-13 / Infra 1.5 #8 —— 成果数，各 agent 自己的单位。只在对应那一行出现：
       * Oscar 的 `reportsCount`（当天 SUCCEEDED 的 operation_report + sourcing_report，不含
       * alert_digest），Anna 的 `answersCount`（当天她回给顾客的消息条数）。
       *
       * <p><b>三种状态，三句不同的话</b>：数字（含 0）是当天的事实；显式 `null` 是后端那次读
       * 失败了（前端说「unavailable」，绝不画成 0）；字段缺席是本字段上线前冻结的旧简报
       * （保持原措辞）。所以类型必须是 `number | null` 且可选，三者不能折成一种。
       */
      reportsCount?: number | null;
      answersCount?: number | null;
      /**
       * [需求①] 这个 agent 的任务里，别人一起干的那些 —— 「12 tasks handled (3 with Oscar)」。
       *
       * <p><b>可选，而且会长期可选</b>：没有参与者的行后端整个字段都不发（「(0 with)」不是
       * 一句话），而本字段上线之前composed 的简报全都没有它 —— 消息是<b>冻结</b>的 payload，
       * 不是会重算的视图。所以缺席是回滚历史时的正常情况。
       *
       * <p>`count` 是<b>任务数不是人次</b>：一条任务带两个参与者仍然算一条，
       * 所以它一定 ≤ 同一行的 `total`。`labels` 已经是商家认得的名字（"Oscar"），
       * 与同一行的 `label` 一个口径，前端不再做第二次映射。
       */
      participation?: {
        count: number;
        labels: string[];
      };
    }>;
  };
  /**
   * Segments the server could not fill, named rather than silently dropped.
   *
   * A briefing with no revenue line reads as "your store sold nothing yesterday" unless something
   * says otherwise — so the UI renders one honest line instead of a zero. Today this is always
   * numbers/anomalies/suggestions, because they read from the Shopify data platform (#8), which is
   * not delivered.
   */
  unavailable?: string[];
  unavailableReason?: string;
}

export interface CongratsPayload {
  headline?: string;
  columns?: Array<{ agentType: string; label: string; blurb: string; href: string }>;
}

export interface StoreManagerFeed {
  /** ASCENDING — the feed is drawn chat-style with the newest at the bottom (PRD V1.23). */
  messages: StoreManagerMessage[];
  /**
   * Cursor for the page BEFORE this one — what "Earlier briefings" loads. Null once the merchant has
   * reached their first ever message, so the link can be hidden rather than dead.
   */
  nextCursor: string | null;
  unreadCount: number;
  /** IANA zone the days were grouped in. Label TODAY / YESTERDAY with THIS, not the browser's. */
  timezone: string | null;
}

const base = (userId: string) =>
  `/digital-staff/store-manager/feed/${encodeURIComponent(userId)}`;

export async function getStoreManagerFeed(
  userId: string,
  options: { cursor?: string; limit?: number } = {}
): Promise<StoreManagerFeed> {
  const response = await apiClient.chatbotGet<StoreManagerFeed>(
    base(userId),
    options as Record<string, unknown>
  );
  return (
    response.data ?? { messages: [], nextCursor: null, unreadCount: 0, timezone: null }
  );
}

/**
 * Fired after {@link markStoreManagerFeedRead} clears the feed server-side. The sidebar's unread
 * hook neither polls nor re-keys on the store-manager route (opening the page IS reading it, and the
 * route is already current), so without this signal its dot lingered until the NEXT navigation —
 * "点进去红点没消失". The hook listens for this and drops the dot in place.
 */
export const STORE_MANAGER_READ_EVENT = 'yepai:store-manager-read';

/**
 * Clear the sidebar's red dot.
 *
 * PRD V1.23: 点过即消失且刷新不复现 — so this is a server call, not local state. Returns how many
 * messages it cleared.
 */
export async function markStoreManagerFeedRead(userId: string): Promise<number> {
  const response = await apiClient.chatbotPost<number>(`${base(userId)}/read`, {});
  const cleared = typeof response.data === 'number' ? response.data : 0;
  // Only after the POST resolved: the server has actually stamped the read, so the count really is
  // clear until new messages arrive. Guarded for non-browser (test/SSR) contexts.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(STORE_MANAGER_READ_EVENT));
  }
  return cleared;
}

/**
 * The hour the briefing sweep fires, in the STORE's timezone — `LOCAL_HOUR` in chatbot-api's
 * `StoreManagerBriefingScheduler`. Kept here because the copy states it to the merchant; if the
 * backend moves it, this is the one line to follow.
 *
 * 2026-09-08: the sweep now fires at 08:00 in the MERCHANT's OWN zone (their last-seen browser
 * zone, from `users.city` / `x-kong-timezone`), not the shop's Shopify setting. So this hour is
 * the hour the merchant sees — there is no second clock to name any more, which is why
 * `briefingArrival` collapsed to one string.
 */
export const BRIEFING_LOCAL_HOUR = 8;

/**
 * 一张卡是「哪一天的」。不成形的 day 排在所有正常日期之前（拿不准就别让它赢）。
 *
 * 🔴 外形对不够，必须是**日历上真的存在的那一天**：`2026-09-99` 外形合法、字典序又比
 * `2026-09-30` 大，进了 10 月之后它既不在未来、又永远压着当月每一张真卡（评审第四轮第 6 条）。
 */
function dayKey(message: StoreManagerMessage): string {
  const day = message.day ?? '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  // Date.UTC 会把 09-99 悄悄进位成 12-08，所以反过来核一遍：转回字符串还等于自己才算数。
  const at = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(at.getTime()) || at.toISOString().slice(0, 10) !== day ? '' : day;
}

/** 同一天真撞上两张时的兜底：后写的那张赢。解析不出来的当作最早。 */
function arrivalKey(message: StoreManagerMessage): number {
  const at = Date.parse(message.createdAt);
  return Number.isNaN(at) ? 0 : at;
}

/**
 * 最新的那份简报 —— 按它是**哪一天的**（`day`）挑，不按它什么时候写进库（`createdAt`）。
 *
 * 平时两者同序，例外的那天很贵。2026-09-21 在 dev 上实测：商家 1205844033637781504 的
 * 周一卡带着 Oscar 的运营报告摘要（第四段），却是 09-19T11:12Z 用 local-trigger 提前造出来的，
 * createdAt 比周日那张（09-19T23:00Z 由扫描自动写）**早**。而 feed 是按 createdAt 排的
 * （后端 `order by m.createdAt desc, m.id desc`），于是 `[...messages].reverse().find(…)`
 * 选中了周日那张 —— 周日按设计没有第四段，屏幕上就成了「运营报告没生成」。
 * 补写、回填、灾后补卡、跨时区提前落盘，都是同一个形状。
 *
 * **未来的卡不参选**（`day` 晚于商家当地今天）。同一把 local-trigger 也能按未来日期造卡，
 * 而按 day 挑会让它一直压住之后每天真正生成的卡 —— 那比这里要修的 bug 更糟。整页都是
 * 未来卡时返回 `null`：一份关于还没过完的某天的 payload 不该被当成「最新简报」渲染。
 * （`day` 缺失的卡另算：它只能垫底，但页面会照实说「Latest briefing」，所以不藏。）
 *
 * ⚠️ 只在**调用方拿到的这一页**里挑。那一页是后端按 createdAt 截的（limit=5），
 * 所以一张 day 很新、createdAt 很旧的卡仍可能整个落在页外。真要根治得让 feed 按 day 排，
 * 那会连着改游标分页 —— 是另一件事，不在这里做。
 *
 * @param timezone 商家的时区（`StoreManagerFeed.timezone`），判「未来」用；缺省用浏览器的。
 * @returns 这一页里 day 最大且不在未来的简报；一份都没有时是 `null`（调用方据此画「还没被扫过」）。
 */
export function latestBriefing(
  messages: StoreManagerMessage[],
  timezone?: string | null
): StoreManagerMessage | null {
  const cutoff = latestElapsedDay(timezone);
  let best: StoreManagerMessage | null = null;
  /** 没有可用 `day` 的卡。只有一张有日期的都挑不出来时才轮到它。 */
  let undated: StoreManagerMessage | null = null;
  for (const message of messages) {
    if (message.type !== 'briefing') continue;
    const day = dayKey(message);
    // 说不清自己是哪一天的卡**只能垫底**（评审第五轮 HIGH，但不照单全收）。
    // `''` 比任何 cutoff 都小，光靠下面那道未来门拦不住它，它会压住真正的卡 —— 那要拦。
    // 但「一页只有坏卡时也不给看」是另一回事，那与这一页既有的裁定冲突：`day` 缺失时
    // 仍然渲染，只是顶部写「Latest briefing」、标题写「That day」，不假装知道是哪天
    // （StoreManagerPage.infra15 的「简报没有 day 时顶部不写 Today」）。所以留作兜底。
    if (!day) {
      undated = pickLater(undated, message);
      continue;
    }
    // 还没到的那一天不参选。运维用 local-trigger 按未来日期造卡是真会发生的事
    // （dev 上 09-19 就造出过一张 day=09-21 的卡），而提前把它顶到屏幕上，会让商家在周六
    // 看到一张写着「Sep 21」、被判成陈旧的卡，把当天真正的卡挡住 —— 比这次要修的 bug 更糟。
    // `2026-99-99` 这类外形合法、日历上不存在的日期也一并落在这里。
    if (day > cutoff) continue;
    best = pickLater(best, message);
  }
  // 一张有日期的都没有时才轮到没日期的那张。**未来卡不在兜底之列** —— 那等于把一份
  // 关于还没过完的某天的 payload 当成「最新简报」渲染出来并标记已读，比空着更糟；
  // 而没日期的那张页面会照实说「Latest briefing / That day」，不骗人。
  return best ?? undated;
}

/**
 * 这一页里到底有没有简报 —— 与 {@link latestBriefing} 返回 null 的**两种**原因配套：
 * 「一份都没有」和「有，但全是还没到的那几天」。前者是真的没被扫过、可以清未读；
 * 后者清未读会让那张卡等到日子到了已经没有红点了（评审第四轮第 2 条）。
 */
export function hasAnyBriefing(messages: StoreManagerMessage[]): boolean {
  return messages.some(message => message.type === 'briefing');
}

/**
 * 这份简报是不是**在它自己归档的那一天**写出来的。
 *
 * Rail 上那格叫「Briefing Sent」，只画时分、不画日期 —— 所以它读起来必然是「今天几点发的」。
 * 一张运维提前造的卡（周六 11:12 写、归档在周一）到了周一被选中后，那格会写「11:12 AM」，
 * 商家没有任何线索知道那是上周六（评审第四轮 BLOCK-4 给的反例，它站得住）。
 * 这种时候返回 false，调用方画「—」：PRD #4 本来就允许时间字段在「没有可说的时刻」时画破折号，
 * 说不出口好过说错。
 *
 * ⚠️ 只在**认得商家时区**时才敢说「不是同一天」：`createdAt` 是一个时刻，`day` 是商家日历上
 * 的一天，没有那块表就比不了 —— 拿浏览器的表硬比，会把东京商家早上 8:00 写的卡在纽约看成前一天。
 */
export function writtenOnItsOwnDay(
  message: StoreManagerMessage,
  timezone?: string | null
): boolean {
  if (!zoneIsKnown(timezone)) return true;
  const at = new Date(message.createdAt);
  if (Number.isNaN(at.getTime())) return false;
  return wallClockIn(at, timezone).date === dayKey(message);
}

/** 这个时区名当前运行时认不认得。认不得（或没给）就等于不知道商家的今天是哪一天。 */
function zoneIsKnown(timezone: string | null | undefined): timezone is string {
  if (!timezone) return false;
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * 「已经开始了的最后一天」—— 判未来卡的分界线。
 *
 * 认得商家的表（feed 带回了合法的 IANA 名）就严格切在**他的今天**。认不得时只剩浏览器
 * 这块表，而两者最多差一天：这时**放宽一天**。宁可放过一张提前造的卡，也不能把商家真正的
 * 今天当成未来砍掉 —— 后者会让一个在基里巴斯的商家、用夏威夷的浏览器看时永远少一天。
 * ⚠️ `wallClockIn` 对认不得的时区是**静默**退回浏览器的，所以这里必须自己先验一次。
 */
function latestElapsedDay(timezone: string | null | undefined): string {
  if (zoneIsKnown(timezone)) return wallClockIn(new Date(), timezone).date;
  // 🔴 「明天」按日历翻页，不是 `Date.now() + 24h`：DST 回拨那天 +24h 还落在同一天
  // （纽约 2026-11-01 00:30 EDT + 24h = 当天 23:30 EST），放宽的那一天会凭空消失（评审第四轮第 1 条）。
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return wallClockIn(tomorrow, null).date;
}

/** 两张卡里更「新」的那张：先比 day，同一天再比写入时刻。 */
function pickLater(
  best: StoreManagerMessage | null,
  message: StoreManagerMessage
): StoreManagerMessage {
  if (!best) return message;
  const day = dayKey(message);
  const bestDay = dayKey(best);
  if (day > bestDay || (day === bestDay && arrivalKey(message) > arrivalKey(best))) {
    return message;
  }
  return best;
}

/**
 * Which day a message is FILED under — the key the separators group on.
 *
 * Monica 2026-09-08: group by the day the briefing is ABOUT, not the day it arrived. A merchant
 * looking for "how did the 6th go" should find it under the 6th. Arrival was never the thing they
 * were looking for, and putting the two side by side read as a contradiction: a card titled
 * "Your AI team — 6 Sept" sitting under a separator that said YESTERDAY (= the 7th), because a
 * briefing reports on the day BEFORE it is pushed.
 *
 * Falls back to the arrival day for messages that report on nothing — the onboarding congrats has
 * no `reportedOn`, and it belongs on the day it showed up.
 */
export function reportedDayOf(message: StoreManagerMessage): string {
  const reported = (message.payload as { reportedOn?: string } | null)?.reportedOn;
  if (reported && /^\d{4}-\d{2}-\d{2}$/.test(reported)) return reported;
  return browserDayOf(message.createdAt) ?? message.day;
}

/**
 * Which browser day an arrival instant fell on. Now only the fallback for `reportedDayOf` and the
 * rail's rough age — the separators no longer group on arrival at all.
 */
export function browserDayOf(createdAt: string): string | null {
  const at = new Date(createdAt);
  return Number.isNaN(at.getTime()) ? null : wallClockIn(at, null).date;
}

/**
 * The separator label for a message's day, in the READER'S timezone.
 *
 * PRD V1.23 shows "TODAY · 8:00 AM" / "YESTERDAY · 8:00 AM". Two decisions stacked here:
 *
 * - Monica 2026-09-03: whichever day this is, it is read on the BROWSER's clock, every surface.
 *   (That reversed an earlier call to use the store's day so a colleague one timezone east would
 *   still see the store's Monday as "Monday" — the cost was the commoner reading, a merchant
 *   abroad seeing "TODAY" on a briefing their own day had not reached.)
 * - Monica 2026-09-08: the day passed in is the day the briefing is ABOUT (`reportedDayOf`), not
 *   the day it arrived. So "YESTERDAY" here means "yesterday's business" — which is exactly what
 *   the card underneath already says, and the two no longer contradict each other.
 */
export function dayLabel(day: string, timezone?: string | null): string {
  // ⚠️ timezone 不是可选的装饰。合并 Infra1.5 时这个参数在 dev 侧的重写里被去掉、内部写死了
  // null，于是「TODAY」按**看板这台机器**的午夜判断。dev 店在纽约，本机在东八区 —— 同一份
  // 简报在中国下午看是 TODAY、在纽约上午看就变成 YESTERDAY，两边谁都不会觉得自己看错了。
  // 调用方（StoreManagerPage）一直按两参数在调，对不上只是恰好被 tsc 挡住了。
  const zone = timezone || null;
  const today = wallClockIn(new Date(), zone).date;
  if (day === today) return 'TODAY';
  if (day === wallClockIn(new Date(Date.now() - 24 * 60 * 60 * 1000), zone).date) return 'YESTERDAY';

  // Anything older reads better as a date than as "3 days ago" — this is a work record.
  // 'en-US'：拼进英文句子里的日期，跟随浏览器语言会变成「2026年8月2日」。
  return new Date(`${day}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * "8:00 AM" beside the day label — when it landed, on the reader's clock.
 *
 * The bad-input guard is an explicit NaN check, not a try/catch: `toLocaleTimeString` on an invalid
 * Date does not throw, it returns the string "Invalid Date" — so the catch this function used to
 * rely on never fired and that text went straight onto the card.
 */
export function timeLabel(createdAt: string): string {
  const at = new Date(createdAt);
  if (Number.isNaN(at.getTime())) return '';
  // 'en-US' 而不是 undefined：卡片上这个时刻旁边全是写死的英文，跟随浏览器语言会让中文机器
  // 上渲染成「上午8:00」，一行里两种排版。
  return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * When the next briefing lands, on the merchant's own clock — which is now the only clock involved.
 *
 * Until 2026-09-08 the sweep fired at 08:00 in the SHOP's zone, so this had to return two strings
 * and every copy site had to word "…and 8:00 AM at the store" for a merchant who was elsewhere.
 * The sweep now fires at 08:00 in the merchant's OWN zone (their last-seen browser zone), so
 * there is no second clock to name and no promise to hedge: 8:00 AM is 8:00 AM.
 *
 * A function rather than a bare constant because the hour still belongs to the backend
 * (`StoreManagerBriefingScheduler.LOCAL_HOUR`) — if it moves, this formats the new one.
 */
export function briefingArrival(): string {
  const at = new Date();
  at.setHours(BRIEFING_LOCAL_HOUR, 0, 0, 0);
  // 'en-US'：整句是写死的英文（"Briefings arrive at …"），跟随浏览器语言会渲染成
  // 「Briefings arrive at 上午8:00」；中文 locale 默认还是 24 小时制，连 AM 都没了。
  return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
